import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { FaPlay } from "react-icons/fa";
import { FaPause } from "react-icons/fa6";
import Cookies from "js-cookie";
import appVersion from "../../version";
import CloudComponent from "../CloudComponent";
import Cloud from "../Cloud";
import Header from "./Header";
import LanguageSelector from "./LanguageSelector";

const apiUrl = process.env.REACT_APP_API_URL;
const apiKey = process.env.REACT_APP_AUTH_TOKEN;

const translations = {
  en: {
    selectLanguage: "Select Language:",
    verifyOtpTitle: "Verify OTP",
    verifyOtpDescription:
      "We have sent an OTP to your phone number. Please enter it below to verify.",
    otpPlaceholder: "Enter OTP",
    verifyOtp: "Verify OTP",
    resendOtp: "Resend OTP",
    otpViaCall: "OTP via Call",
    invalidOtp: "Invalid OTP",
  },
  Hi: {
    selectLanguage: "भाषा चुनें:",
    verifyOtpTitle: "OTP सत्यापित करें",
    verifyOtpDescription:
      "हमने आपके फोन नंबर पर OTP भेजा है। कृपया सत्यापन के लिए नीचे दर्ज करें।",
    otpPlaceholder: "OTP दर्ज करें",
    verifyOtp: "OTP सत्यापित करें",
    resendOtp: "OTP पुनः भेजें",
    otpViaCall: "कॉल के माध्यम से OTP",
    invalidOtp: "अमान्य OTP",
  },
  Kn: {
    selectLanguage: "ಭಾಷೆ ಆಯ್ಕೆ ಮಾಡಿ:",
    verifyOtpTitle: "OTP ಪರೀಲನೆ", 
    verifyOtpDescription:"ನಾವು ನಿಮ್ಮ ಮೊಬೈಲ್‌ ಸಂಖ್ಯೆಗೆ OTP ಯನ್ನು ಕಳುಹಿಸಿದ್ದೇವೆ. ದಯವಿಟ್ಟು ಅದನ್ನು ಪರಿಶೀಲಿಸಲು ಕೆಳಗೆ ನೀಡಿರುವ ಜಾಗದಲ್ಲಿ ನಮೂದಿಸಿ.",
      otpPlaceholder: "OTP ನಮೂದಿಸಿ",
    verifyOtp: "OTP ಪರಿಶೀಲಿಸಿ",
    resendOtp: "OTP ಮರುಕಳುಹಿಸಿ",
    otpViaCall: "ಕಾಲ್ ಮೂಲಕ OTP",
    invalidOtp: "ಅಮಾನ್ಯ OTP",
  },
};

const OtpInput = () => {
  const [otp, setOtp] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [resendDisabled, setResendDisabled] = useState(true);
  const [countdown, setCountdown] = useState(30);
  const [isPlaying, setIsPlaying] = useState(false);
    const selectedLang = Cookies.get('selectedLanguage') || 'en';
    const [language, setLanguage] = useState("en");
  
    useEffect(() => {
      const cookieLang = Cookies.get("selectedLanguage") || "en";
      setLanguage(cookieLang);
    }, []);
    
  const navigate = useNavigate();
  const audioRef = useRef(null);
  const app_ver = appVersion.app_ver;
  
  const otp_id = sessionStorage.getItem("otp_id");
  const vist_id = Cookies.get("visitor_id");

  // Handle OTP input change
  const handleChange = (e) => {
    const inputValue = e.target.value;
    if (/^\d*$/.test(inputValue) && inputValue.length <= 6) {
      setOtp(inputValue);
    }
  };

  // Countdown effect for enabling resend buttons
  useEffect(() => {
    if (resendDisabled && countdown > 0) {
      const timer = setInterval(() => {
        setCountdown((prev) => prev - 1);
      }, 1000);
      return () => clearInterval(timer);
    } else {
      setResendDisabled(false);
    }
  }, [resendDisabled, countdown]);

  // Function to verify OTP
  const handleVerifyOtp = async () => {
    try {
      const response = await fetch(`${apiUrl}/v1/ccrim_bot_otp_verify`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          api_key: apiKey,
          otp_code: otp,
          otp_id: otp_id,
          visitor_token: vist_id,
          app_ver: app_ver,
          bot_lang_id: selectedLang=== "en" ? "2" : selectedLang === "Hi" ? "3" : selectedLang === "Kn" ? "1" : "4",
        }),
      });
      const data = await response.json();
      if (data?.resp?.error_code === "0") {
        sessionStorage.setItem("otp_id", data.resp.otp_id);
        setErrorMessage("");
        Cookies.set("bot_token", data.resp.bot_token, {
          path: "/",
          sameSite: "Strict",
          expires: 7,
        });
        navigate("/questions", { replace: true });
      } else {
        setErrorMessage(translations[selectedLang].invalidOtp);
      }
    } catch (error) {
      console.error("Error during OTP verification:", error);
    }
  };

  // Function to resend OTP
  const handleResendOtp = async (type) => {
    try {
      setResendDisabled(true);
      setCountdown(20);
      const response = await fetch(`${apiUrl}/v1/ccrim_otp_resend`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          api_key: apiKey,
          otp_id: otp_id,
          visitor_token: vist_id,
          type: type,
          app_ver: app_ver,
          bot_lang_id: selectedLang=== "en" ? "2" : selectedLang === "Hi" ? "3" : selectedLang === "Kn" ? "1" : "4",
        }),
      });
      const data = await response.json();
      if (data?.resp?.error_code === "0") {
        sessionStorage.setItem("otp_id", data.resp.otp_id);
        setErrorMessage("");
      } else {
        setErrorMessage("Failed to resend OTP");
      }
    } catch (error) {
      console.error("Error during OTP resend:", error);
    }
  };

  // Audio toggle functions
  const toggleAudio = (e) => {
    e.preventDefault();
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
        setIsPlaying(false);
      } else {
        audioRef.current.play();
        setIsPlaying(true);
      }
    }
  };

  const handleAudioEnd = () => {
    setIsPlaying(false);
  };
  const audioMap = {
    en: "audio/English BOT Audios/otp.mp3",
    Hi: "audio/Hindi BOT Audios/Hindi-Audio2.mp3",
    Kn: "audio/Kannada BOT Audios/Kannada-Audio2.m4a",
    tcy: "audio/cybervictim_tcy.mp3"
  };

  useEffect(() => {
    // Change audio source when selectedLang changes
    if (audioRef.current) {
      const audioSource = audioMap[selectedLang] || audioMap.en;
      audioRef.current.src = audioSource;
      audioRef.current.load(); // Reload the audio element to apply new source
    }
  }, [selectedLang]);
  
  return (
    <div className="otp-container">
      <Header />
      
      <LanguageSelector onLanguageChange={setLanguage} />
      <div className="container">
        <div style={{ display: "flex", justifyContent:'center' }}>
          
          <h1>{translations[selectedLang].verifyOtpTitle}</h1>
          <button onClick={toggleAudio} className="audio-button">
            {isPlaying ? <FaPause color="White" size={12} style={{ marginBottom: '20px' }} /> : <FaPlay color="White" size={12} style={{ marginBottom: '20px' }}/>}
          </button>
        </div>
        <audio ref={audioRef} onEnded={handleAudioEnd}>
          <source src={audioMap[selectedLang]} type="audio/mp3" />
          Your browser does not support the audio element.
        </audio>
        <p>{translations[selectedLang].verifyOtpDescription}</p>
        <input
          className="otp-input"
          type="text"
          placeholder={translations[selectedLang].otpPlaceholder}
          value={otp}
          onChange={handleChange}
          maxLength="6"
          name="otp"
        />
        {errorMessage ? (
          <p className="error-message">{errorMessage}</p>
        ) : (
          <p></p>
        )}
        <button
          onClick={handleVerifyOtp}
          className="otp-btn"
          disabled={otp.length !== 6}
          style={{
            opacity: otp.length !== 6 ? 0.5 : 1,
            cursor: otp.length !== 6 ? "not-allowed" : "pointer",
          }}
        >
          {translations[selectedLang].verifyOtp}
        </button>
        {countdown <= 0 ? (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <button
              onClick={() => handleResendOtp("SMS")}
              className="resend-btn"
              disabled={resendDisabled}
            >
              {translations[selectedLang].resendOtp}
            </button>
            <p style={{ color: "blue", marginTop: "30px" }}> | </p>
            <button
              onClick={() => handleResendOtp("Call")}
              className="resend-btn"
              disabled={resendDisabled}
            >
              {translations[selectedLang].otpViaCall}
            </button>
          </div>
        ) : (
          <p style={{ color: "rgb(0, 106, 255)" }}>
            00:{String(countdown).padStart(2, "0")}s
          </p>
        )}
      </div>

      <div style={{ position: "fixed", bottom: "0", left: "0" }}>
        <CloudComponent />
      </div>
      <div style={{ position: "fixed", bottom: "0", right: "0" }}>
        <Cloud />
      </div>
    </div>
  );
};

export default OtpInput;
